import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { Button } from '@heroui/button';
import { Tab, Tabs } from '@heroui/tabs';
import { addToast } from '@heroui/toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { billsApi, getClassification } from '@/api/bills';
import { billImportsControllerFindOne } from '@/api/generated/bill-imports/bill-imports';
import { useBillsControllerReclassify } from '@/api/generated/bills/bills';
import { ReclassifyBillDtoClassification } from '@/api/generated/schemas';
import {
  BillActionsBar,
  BillChargesTab,
  BillDetailsTab,
  BillHeader,
  BillMetersTab,
  BillReclassifyModal,
  BillStatsOverview,
  BillStatusBanner,
  DeleteBillModal,
  EditBillPeriodModal,
} from '@/components/bills';
import { Icon } from '@/components/common';
import { useBill, useBillLinkedSites } from '@/queries/bills.queries';

export const Route = createFileRoute('/bills/$billId')({
  component: BillDetail,
});

function BillDetail() {
  const { billId } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: bill, isLoading } = useBill(billId);
  const { data: linkedSites = [] } = useBillLinkedSites(billId);

  // Modal states
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState(false);
  const [selectedClassification, setSelectedClassification] = useState<ReclassifyBillDtoClassification | ''>('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editPeriodModalOpen, setEditPeriodModalOpen] = useState(false);

  // Poll for bill import status when bill has an import ID
  const { data: billImport } = useQuery({
    queryKey: ['billImport', bill?.billImportId],
    queryFn: () => (bill?.billImportId ? billImportsControllerFindOne(bill.billImportId) : null),
    enabled: !!bill?.billImportId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      // Poll every 2 seconds if reclassifying or processing
      if (status === 'reclassifying' || status === 'processing') {
        return 2000;
      }
      // Don't poll otherwise
      return false;
    },
  });

  // Check if bill is being reclassified
  const isReclassifying = billImport?.status === 'reclassifying';

  // Use pre-calculated billing period warning flag from backend
  const exceedsThreshold = bill?.hasBillingPeriodWarning ?? false;

  // When reclassification completes, refresh the bill data and navigate to new bill if changed
  useEffect(() => {
    if (billImport?.status === 'completed' && billImport?.billId && billImport.billId !== billId) {
      // Bill was reclassified and replaced with a new bill
      addToast({
        title: 'Success',
        description: 'Bill has been successfully reclassified and reprocessed',
        color: 'success',
      });
      // Navigate to the new bill
      navigate({ to: '/bills/$billId', params: { billId: billImport.billId } });
      // Invalidate queries
      queryClient.invalidateQueries({ queryKey: ['bills'] });
    }
  }, [billImport?.status, billImport?.billId, billId, navigate, queryClient]);

  // Reclassify mutation
  const reclassifyMutation = useBillsControllerReclassify<Error>({
    mutation: {
      onSuccess: () => {
        addToast({
          title: 'Success',
          description: 'Your bill is being reclassified',
          color: 'success',
        });
        // Invalidate the bill import query to start polling
        queryClient.invalidateQueries({ queryKey: ['billImport', bill?.billImportId] });
        // Close modal
        setReclassifyModalOpen(false);
        setSelectedClassification('');
      },
      onError: (error) => {
        console.error('Reclassify error:', error);
        addToast({
          title: 'Error',
          description: error.message ?? 'An unexpected error occurred',
          color: 'danger',
        });
      },
    },
  });

  // Download PDF mutation
  const downloadPdfMutation = useMutation({
    mutationFn: () => {
      if (!bill) throw new Error('Bill not found');
      return billsApi.downloadBillPdf(bill);
    },
    onError: (error) => {
      console.error('Download error:', error);
      addToast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to download PDF',
        color: 'danger',
      });
    },
  });

  const handleOpenReclassify = () => {
    const currentClassification = bill ? getClassification(bill.billType, bill.utilityType) : '';
    setSelectedClassification(currentClassification);
    setReclassifyModalOpen(true);
  };

  const handleReclassify = () => {
    if (!selectedClassification) return;
    reclassifyMutation.mutate({ id: billId, data: { classification: selectedClassification } });
  };

  if (isLoading) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <p className="text-gray-500">Loading bill details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!bill) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center py-12">
            <p className="text-gray-500">Bill not found</p>
            <Button
              variant="light"
              startContent={<Icon as={ArrowLeftIcon} size="sm" color="inherit" />}
              className="mt-4"
              onPress={() => navigate({ to: '..' })}
            >
              Back
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Status Banners */}
        <BillStatusBanner billImport={billImport} isReclassifying={isReclassifying} />

        {/* Header */}
        <BillHeader bill={bill} onBack={() => navigate({ to: '..' })} />

        {/* Stats Overview */}
        <BillStatsOverview bill={bill} exceedsThreshold={exceedsThreshold} />

        {/* Tabs */}
        <Tabs aria-label="Bill details" className="w-full">
          <Tab key="details" title="Bill Details">
            <BillDetailsTab bill={bill} linkedSites={linkedSites} onEditPeriod={() => setEditPeriodModalOpen(true)} />
          </Tab>

          <Tab key="charges" title="Charges">
            <BillChargesTab bill={bill} />
          </Tab>

          <Tab key="meters" title="Meters">
            <BillMetersTab bill={bill} />
          </Tab>
        </Tabs>

        {/* Actions */}
        <BillActionsBar
          bill={bill}
          isReclassifying={isReclassifying}
          isDownloading={downloadPdfMutation.isPending}
          onDownloadPdf={() => downloadPdfMutation.mutate()}
          onReclassify={handleOpenReclassify}
          onDelete={() => setDeleteModalOpen(true)}
        />

        {/* Reclassify Modal */}
        {bill && (
          <BillReclassifyModal
            bill={bill}
            isOpen={reclassifyModalOpen}
            onClose={() => setReclassifyModalOpen(false)}
            selectedClassification={selectedClassification}
            onClassificationChange={setSelectedClassification}
            onReclassify={handleReclassify}
            isLoading={reclassifyMutation.isPending}
          />
        )}

        {/* Edit Billing Period Modal */}
        {bill && (
          <EditBillPeriodModal isOpen={editPeriodModalOpen} onClose={() => setEditPeriodModalOpen(false)} bill={bill} />
        )}

        {/* Delete Confirmation Modal */}
        {bill && (
          <DeleteBillModal
            isOpen={deleteModalOpen}
            onClose={() => setDeleteModalOpen(false)}
            bill={bill}
            linkedSites={linkedSites}
            onDeleted={() => navigate({ to: '/bills' })}
          />
        )}
      </div>
    </div>
  );
}
